import React, { useEffect, useRef } from 'react';

export default function LimitedTextarea({
  value,
  onChange,
  max = 240,
  className = '',
  rows = 3,
  placeholder = '',
  wrapperClassName = '',
  counterClassName = '',
  autoGrow = false,
  minRows = rows,
  maxRows,
  inputRef = null,
  ...rest
}) {
  const val = value || '';
  const remaining = Math.max(0, max - val.length);

  const handleChange = (e) => {
    const next = e.target.value.slice(0, max);
    onChange?.({ target: { value: next } });
  };

  const handlePaste = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData('text');
    const next = (val + paste).slice(0, max);
    e.preventDefault();
    onChange?.({ target: { value: next } });
  };

  const wrapperClasses = ['limited-textarea', wrapperClassName].filter(Boolean).join(' ');
  const counterClasses = ['char-counter', counterClassName].filter(Boolean).join(' ');
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!inputRef) return;
    if (typeof inputRef === 'function') {
      inputRef(textareaRef.current);
      return () => inputRef(null);
    }
    inputRef.current = textareaRef.current;
    return () => {
      if (inputRef && typeof inputRef !== 'function') {
        inputRef.current = null;
      }
    };
  }, [inputRef]);

  useEffect(() => {
    if (!autoGrow || !textareaRef.current) return;
    const el = textareaRef.current;

    const computeHeightForRows = (rowsCount) => {
      if (!rowsCount || typeof window === 'undefined') return null;
      const style = window.getComputedStyle(el);
      const lineHeight = parseFloat(style.lineHeight || '0');
      const paddingTop = parseFloat(style.paddingTop || '0');
      const paddingBottom = parseFloat(style.paddingBottom || '0');
      const borderTop = parseFloat(style.borderTopWidth || '0');
      const borderBottom = parseFloat(style.borderBottomWidth || '0');
      return rowsCount * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
    };

    const minHeight = computeHeightForRows(minRows);
    const maxHeight = computeHeightForRows(maxRows);

    // Reset height so scrollHeight measures the full content.
    el.style.height = 'auto';
    const nextHeight = Math.max(minHeight || 0, el.scrollHeight);
    const finalHeight = maxHeight ? Math.min(nextHeight, maxHeight) : nextHeight;
    el.style.height = `${finalHeight}px`;
    el.style.overflowY = maxHeight && nextHeight > maxHeight ? 'auto' : 'hidden';
  }, [val, autoGrow, rows, minRows, maxRows]);

  return (
    <div className={wrapperClasses}>
      <textarea
        ref={textareaRef}
        value={val}
        onChange={handleChange}
        onPaste={handlePaste}
        maxLength={max}           // native guard
        rows={rows}
        placeholder={placeholder}
        className={className}
        aria-label={placeholder || 'text area'}
        {...rest}
      />
      <div className={counterClasses}>{remaining} / {max}</div>
    </div>
  );
}
